import { z } from "zod";
/**
 * Wire-format DTO for the admin dashboard `/today` endpoint (P08-T9 +
 * pre-launch expansion).
 *
 * Single SQL query (CTE-driven) returning the operational stat tiles for
 * the admin dashboard home. No caching, no real charts — V1.0 is "what's
 * happening right now, with a small revenue trend for context". Plan 11
 * (V1.5 reports) takes the full multi-range / charting path with proper
 * indexes and a daily-revenue snapshot table.
 *
 * Field naming: snake_case verbatim per spec §141. Numeric fields are
 * primitive numbers; BIGINT columns (revenue_*) come back from postgres-js
 * as strings and are converted to Number at the service boundary with the
 * documented 2^53 ≈ £90T safety cap (V1.0 GBP-only).
 *
 * Scalar tiles:
 *   - `orders_today` — count of orders with `placed_at >= todayStart`.
 *   - `revenue_today_minor` — SUM of `total_minor` for orders with
 *     `status IN ('paid','fulfilled','partially_refunded','refunded')
 *      AND placed_at >= todayStart`. The status filter widens beyond
 *     `status='paid'` so post-payment status transitions (fulfil, refund)
 *     don't make today's revenue tile undercount.
 *   - `pending_customers` — count of customers with `approval_status='pending'`.
 *   - `fulfilment_queue` — count of orders with `status='paid'`.
 *   - `low_stock_count` — count of variants with
 *     `stock_quantity <= 5 AND deleted_at IS NULL`. Hardcoded threshold
 *     for V1.0; configurable in V1.5+.
 *   - `pending_refunds_count` — count of refunds with `status='pending'`.
 *     Surfaces stuck Stripe-side settlements (the Stripe-Link bug fixed
 *     in P08-T14 fix-pass would have shown up here pre-fix).
 *   - `pending_applications_count` — count of `customer_applications` rows
 *     with `status='pending'`. Powers the "Applications · N" nav badge in
 *     the admin SPA (P12-T9). Excludes approved and rejected applications.
 *
 * Revenue trend:
 *   - `range_days` — echoes the requested range (7 or 30 in V1.0).
 *   - `revenue_series_minor` — array of length `range_days`. Element i
 *     is the SUM of `total_minor` for orders placed on day `today - (range_days - 1 - i)`
 *     with the same broad post-payment status filter. Array is
 *     0-filled for days with no revenue. Order: oldest-first.
 *
 * "Today" + day boundaries are `(now() AT TIME ZONE 'Europe/London')::date`
 * so cutoffs respect UK local time regardless of server clock TZ.
 */
export const DashboardTodayResponse = z.object({
    orders_today: z.number().int().nonnegative(),
    revenue_today_minor: z.number().int().nonnegative(),
    pending_customers: z.number().int().nonnegative(),
    fulfilment_queue: z.number().int().nonnegative(),
    low_stock_count: z.number().int().nonnegative(),
    pending_refunds_count: z.number().int().nonnegative(),
    pending_applications_count: z.number().int().nonnegative(),
    range_days: z.union([z.literal(7), z.literal(30)]),
    revenue_series_minor: z.array(z.number().int().nonnegative()),
});
/**
 * Query schema for `GET /v1/admin/dashboard/today`. The optional `range`
 * parameter selects the revenue series window. V1.0 supports 7 or 30 days
 * only — multi-range / multi-bucket reports are V1.5 (Plan 11).
 *
 * Default = 7 to preserve the V1.0-T9 behavior for clients that don't
 * pass the param.
 */
export const DashboardTodayQuery = z.object({
    range: z.coerce
        .number()
        .int()
        .refine((n) => n === 7 || n === 30, {
        message: "range must be 7 or 30",
    })
        .default(7),
});
